/**
 * Where a mark is on screen right now, for anything anchored to it from outside the canvas.
 *
 * A DOM view can be measured - an element has a box - and a canvas view cannot, so it has to be
 * asked. The question is worth answering here rather than declining it: these marks move without
 * anything scrolling, through a settling solve and through a re-arrangement, so a tether to one
 * that could not follow would be wrong for the whole of both.
 *
 * Projected through `toViewProjectionMatrix`, the same matrix `findNetworkNodeAt` uses and the one
 * the shader is asserted equal to, so where a mark is said to be and where it was drawn are the
 * same answer.
 */

import type { Size, ViewCamera } from '../../types';
import {
  applyViewProjection,
  clipToScreen,
  toViewProjectionMatrix,
} from '../../geometry/viewProjection';
import { NODE_FLOATS, type NetworkInstances } from './packNetwork';

export type LocateNetworkMarkArgs = {
  instances: NetworkInstances;
  camera: ViewCamera;
  /** In CSS pixels. */
  viewport: Size;
  /** Index into `positions`, which the instances are parallel to. */
  index: number;
};

/**
 * The mark's centre in canvas-local CSS pixels, or `null` when it is behind the eye.
 *
 * Behind the eye a perspective divide flips the sign and reports a point on the opposite side of
 * the canvas, which is a confident wrong answer - worse than none, since a caller that gets
 * `null` keeps whatever it had rather than moving to somewhere the mark is not.
 */
export const locateNetworkMark = ({
  instances,
  camera,
  viewport,
  index,
}: LocateNetworkMarkArgs): { x: number; y: number } | null => {
  if (index < 0 || index >= instances.nodeCount) return null;
  if (viewport.width === 0 || viewport.height === 0) return null;

  const matrix = toViewProjectionMatrix(camera, viewport);
  const at = index * NODE_FLOATS;
  const clip = applyViewProjection(
    matrix,
    instances.nodes[at],
    instances.nodes[at + 1],
    instances.nodes[at + 2]
  );
  if (clip.w <= 0) return null;

  const { screenX, screenY } = clipToScreen(clip, viewport);
  return { x: screenX, y: screenY };
};
